/**
 * KPI alerts in the top-bar bell (owner research P0-2: in-app first).
 * Mounted once in the shell for users with reports.view in the selected
 * offices. Reads the open alerts every few minutes (the API scans when its
 * data changed) and keeps one bell line per alert, keyed by the alert, so a
 * repeat read replaces rather than duplicates it, and an acknowledged or
 * resolved alert leaves the bell. Lines carry office, metric and numbers
 * only, never a patient.
 */
import type { KpiAlert } from '@velora/contracts/owner-insights';
import { useEffect, useRef } from 'react';
import { insightKeys, insightsApi } from '../../modules/analytics/insights-api.js';
import { useOfficeQuery } from '../office/useOfficeQuery.js';
import { useReportsVisible } from '../session/session.js';
import { notifications, type NotificationInput } from './store.js';

export const KPI_ALERT_POLL_MS = 5 * 60_000;
const KEY_PREFIX = 'kpi-alert:';

/**
 * One open alert per kind and office: the newest week's (ties: the latest
 * seen). An older week stays open while the newest week still trips the same
 * rule, which showed "Adjustment spike at McLaren" twice on Home (owner
 * 2026-10-20). The Alerts page still lists every week. Pure.
 */
export function latestPerKindAndOffice(alerts: readonly KpiAlert[]): KpiAlert[] {
  const best = new Map<string, KpiAlert>();
  for (const a of alerts) {
    if (a.status !== 'open') continue;
    const key = `${a.kind}:${a.locationId}`;
    const b = best.get(key);
    if (
      !b ||
      a.weekStart > b.weekStart ||
      (a.weekStart === b.weekStart && a.lastSeenAt > b.lastSeenAt)
    ) {
      best.set(key, a);
    }
  }
  return alerts.filter((a) => best.get(`${a.kind}:${a.locationId}`) === a);
}

/** Bell lines for open alerts, critical raised last so it sits on top of the bell (pure). */
export function alertBellItems(
  alerts: readonly KpiAlert[],
): Array<NotificationInput & { key: string }> {
  return latestPerKindAndOffice(alerts)
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? 1 : -1))
    .map((a) => ({
      key: `${KEY_PREFIX}${a.id}`,
      title: a.title,
      body: a.recommendedAction,
      tone: a.severity === 'critical' ? 'danger' : 'warning',
      href: `/analytics/alerts?alert=${a.id}`,
    }));
}

export function KpiAlertNotifier() {
  const allowed = useReportsVisible({ mode: 'any' });
  const q = useOfficeQuery({
    queryKey: insightKeys.alerts('open'),
    queryFn: (c, s) => insightsApi.alerts(c, 'open', s),
    enabled: allowed,
    refetchInterval: KPI_ALERT_POLL_MS,
    staleTime: 60_000,
    retry: false,
  });
  const shown = useRef(new Set<string>());
  useEffect(() => {
    if (!q.data) return;
    const items = alertBellItems(q.data.alerts);
    const now = new Set(items.map((i) => i.key));
    for (const key of shown.current) if (!now.has(key)) notifications.resolve(key);
    // Only new lines are raised (a repeat read must not mark a read line unread).
    for (const i of items) if (!shown.current.has(i.key)) notifications.notify(i);
    shown.current = now;
  }, [q.data]);
  return null;
}
